// Sound management service for user-recorded sounds

import { Sound } from '../types/Sound'

class SoundManager {
  private sounds: Sound[] = []
  private storageKey = 'soundBoard_userSounds'

  constructor() {
    this.loadSounds()
  }

  // Load sounds from localStorage
  private loadSounds(): void {
    try {
      const stored = localStorage.getItem(this.storageKey)
      if (stored) {
        this.sounds = JSON.parse(stored)
        console.log(`Loaded ${this.sounds.length} user sounds`)
      }
    } catch (error) {
      console.error('Failed to load sounds from storage:', error)
      this.sounds = []
    }
  }

  // Save sounds to localStorage
  private saveSounds(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.sounds))
      console.log(`Saved ${this.sounds.length} sounds to storage`)
    } catch (error) {
      console.error('Failed to save sounds to storage:', error)
    }
  }

  // Get all sounds
  getSounds(): Sound[] {
    return [...this.sounds]
  }

  // Add a new sound
  addSound(blob: Blob, label: string, category: string = 'custom'): Promise<Sound> {
    return new Promise((resolve, reject) => {
      try {
        // Create unique ID
        const id = `sound_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

        // Create object URL for the blob
        const audioURL = URL.createObjectURL(blob)

        // Create sound object
        const sound: Sound = {
          id,
          name: id,
          label: label.trim(),
          category,
          filePath: audioURL, // Use blob URL
          createdAt: new Date().toISOString()
        }

        // Add to sounds array
        this.sounds.push(sound)

        // Save to storage
        this.saveSounds()

        console.log(`Added new sound: ${label}`)
        resolve(sound)
      } catch (error) {
        console.error('Failed to add sound:', error)
        reject(error)
      }
    })
  }

  // Remove a sound
  removeSound(soundId: string): boolean {
    try {
      const soundIndex = this.sounds.findIndex(sound => sound.id === soundId)
      if (soundIndex === -1) {
        return false
      }

      const sound = this.sounds[soundIndex]

      // Revoke the blob URL to free memory
      if (sound.filePath.startsWith('blob:')) {
        URL.revokeObjectURL(sound.filePath)
      }

      // Remove from array
      this.sounds.splice(soundIndex, 1)

      // Save to storage
      this.saveSounds()

      console.log(`Removed sound: ${sound.label}`)
      return true
    } catch (error) {
      console.error('Failed to remove sound:', error)
      return false
    }
  }

  // Update sound label
  updateSoundLabel(soundId: string, newLabel: string): boolean {
    try {
      const sound = this.sounds.find(s => s.id === soundId)
      if (!sound) {
        return false
      }

      sound.label = newLabel.trim()
      this.saveSounds()

      console.log(`Updated sound label: ${newLabel}`)
      return true
    } catch (error) {
      console.error('Failed to update sound label:', error)
      return false
    }
  }

  // Get sounds by category
  getSoundsByCategory(category: string): Sound[] {
    return this.sounds.filter(sound => sound.category === category)
  }

  // Get all categories
  getCategories(): string[] {
    const categories = new Set(this.sounds.map(sound => sound.category))
    return Array.from(categories).sort()
  }

  // Get sound count
  getSoundCount(): number {
    return this.sounds.length
  }

  // Clear all sounds
  clearAllSounds(): void {
    try {
      // Revoke all blob URLs
      this.sounds.forEach(sound => {
        if (sound.filePath.startsWith('blob:')) {
          URL.revokeObjectURL(sound.filePath)
        }
      })

      this.sounds = []
      this.saveSounds()

      console.log('Cleared all sounds')
    } catch (error) {
      console.error('Failed to clear sounds:', error)
    }
  }

  // Export sounds as JSON (for backup)
  exportSounds(): string {
    try {
      return JSON.stringify(this.sounds, null, 2)
    } catch (error) {
      console.error('Failed to export sounds:', error)
      return '[]'
    }
  }

  // Import sounds from JSON (for restore)
  importSounds(jsonData: string): boolean {
    try {
      const importedSounds = JSON.parse(jsonData)
      if (!Array.isArray(importedSounds)) {
        throw new Error('Invalid data format')
      }

      // Validate sound objects
      for (const sound of importedSounds) {
        if (!sound.id || !sound.label || !sound.filePath) {
          throw new Error('Invalid sound object')
        }
      }

      // Clear existing sounds
      this.clearAllSounds()

      // Add imported sounds
      this.sounds = importedSounds
      this.saveSounds()

      console.log(`Imported ${importedSounds.length} sounds`)
      return true
    } catch (error) {
      console.error('Failed to import sounds:', error)
      return false
    }
  }
}

// Export singleton instance
export const soundManager = new SoundManager()
